import { IAction, IScheduledJob, IScheduledJobsDto } from "@kuika/kuika-cl-model";
import { AxiosResponse } from "axios";
import _ from "lodash";
import { notifyDesignerLiveToolIdle } from "../../../services/designer-live/designer-live";
import { DesignerPresenceReporter } from "../../../services/designer-live/designer-presence";
import { DesignerSyncService } from "../../../services/designer-live/designer-sync-service";
import { ScheduledJobsService } from "../../../services/scheduled-jobs-service";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

export interface ScheduledJobsProviderState {
  selectedSjId: string | undefined;
  scheduledJobs: IScheduledJob[];
  loadScheduledJobs: (projectId: string) => void;
  /**
   * Persists a job and resolves whether it was stored. A CREATE resolves only once the list holds
   * the new job, and it does not open it: the caller does, after confirming the create (the name
   * sheet holds its ✓ first, then opens the job's editor).
   */
  saveScheduledJob: (sj: IScheduledJob) => Promise<boolean>;
  getSelectedSj: () => IScheduledJob | undefined;
  updateScheduledJobs: (sjDto: IScheduledJobsDto) => void;
  setActiveScheduledJob: (sJobId: string) => void;
  addActionToScheduledJob: (action: IAction, scheduledJob: IScheduledJob, isActionParameterUpdated?: boolean) => void;
  deleteScheduleJobAction: (action: IAction, scheduledJob: IScheduledJob) => void;
  cancelEditingScheduledJob: () => void;
  setActiveCreatingSJobName: (name: string) => void;
  activeCreatingSJobName?: string;
  isCreateSJobActive: boolean;
  setCreateSJobActive: (val: boolean) => void;
}

export class ScheduledJobsProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  ScheduledJobsProviderState
> {
  static defaultState: ScheduledJobsProviderState = {
    selectedSjId: undefined,
    scheduledJobs: [],
    loadScheduledJobs: () => {},
    saveScheduledJob: () => new Promise(() => {}),
    getSelectedSj: () => undefined,
    updateScheduledJobs: () => {},
    setActiveScheduledJob: () => {},
    addActionToScheduledJob: () => {},
    deleteScheduleJobAction: () => {},
    cancelEditingScheduledJob: () => {},
    setActiveCreatingSJobName: () => {},
    activeCreatingSJobName: undefined,
    isCreateSJobActive: true,
    setCreateSJobActive: () => {}
  };

  getProviderValues = (): ScheduledJobsProviderState => {
    return {
      selectedSjId: this.state.selectedSjId,
      scheduledJobs: this.state.scheduledJobs,
      loadScheduledJobs: this.loadScheduledJobs,
      saveScheduledJob: this.saveScheduledJob,
      getSelectedSj: this.getSelectedSj,
      updateScheduledJobs: this.updateScheduledJobs,
      setActiveScheduledJob: this.setActiveScheduledJob,
      addActionToScheduledJob: this.addActionToScheduledJob,
      deleteScheduleJobAction: this.deleteScheduleJobAction,
      cancelEditingScheduledJob: this.cancelEditingScheduledJob,
      setActiveCreatingSJobName: this.setActiveCreatingSJobName,
      activeCreatingSJobName: this.state.activeCreatingSJobName,
      isCreateSJobActive: this.state.isCreateSJobActive,
      setCreateSJobActive: this.setCreateSJobActive
    };
  };

  updateScheduledJobs = (sjDto: IScheduledJobsDto) => {
    const { scheduledJobs } = this.state;
    let scheduledJob = scheduledJobs.find((sj) => sj.id === sjDto.id);

    // let sj = this.convertSJDtoToSJ(sjDto);
    if (scheduledJob) {
      scheduledJob = _.cloneDeep(sjDto);
    }
    const scheduledJobIndex = scheduledJobs.findIndex((sj) => sj.id === sjDto.id);
    if (scheduledJobIndex !== undefined && scheduledJobIndex > -1 && scheduledJob) {
      scheduledJobs[scheduledJobIndex] = scheduledJob;
      this.setState({ scheduledJobs });
    }
  };

  getSelectedSj = () => {
    const { selectedSjId, scheduledJobs } = this.state;
    const scheduledJob = scheduledJobs.find((sj) => sj.id === selectedSjId);
    return scheduledJob;
  };

  loadScheduledJobs = async (projectId: string) => {
    try {
      if (projectId !== undefined) {
        const { project } = this.state;
        const res = await ScheduledJobsService.GetAllByProjectId(projectId);

        if (res && res.status === 200) {
          const scheduledJobs = res.data;
          scheduledJobs.forEach((sj) => {
            if (sj.json) {
              sj.json = JSON.parse(sj.json);
            }
          });
          this.setState({ scheduledJobs });
          project.scheduledJobs = scheduledJobs;
        }
        this.partial.projectProvider.updateProject(project);
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  saveScheduledJob = async (sj: IScheduledJob): Promise<boolean> => {
    if (sj === undefined) {
      KMainFunctions.displayErrorNotification("An error occurred while saving scheduled job.");
      return false;
    }

    const previousJob = this.state.scheduledJobs?.find((sJob) => sJob.id === sj.id);

    // Stale-write gate only for UPDATES of an existing job — creates cannot conflict.
    if (previousJob) {
      const proceed: boolean = await DesignerSyncService.checkStaleWrite(
        "scheduledJob.changed",
        sj.id,
        sj.name ?? "Scheduled job"
      );
      if (!proceed) return false;
    }

    if (sj) {
      // Central StartProcess trigger sync for SCHEDULED JOB create/edit/delete. Every
      // UI (addActionToScheduledJob / deleteScheduleJobAction) and MCP path funnels
      // through here, so a whole-job diff keeps the reverse index consistent without
      // per-call-site hooks. Computed before the Update call reassigns `sj` to the
      // server response. Callers pass cloned jobs, so previousJob (state) stays intact.
      const parseJobActions = (job: IScheduledJob | undefined): IAction[] => {
        if (!job?.json) return [];
        try {
          const json = typeof job.json === "string" ? JSON.parse(job.json) : job.json;
          return Array.isArray(json?.actions) ? json.actions : [];
        } catch {
          return [];
        }
      };
      const triggerSync = this.partial.startProcessTriggerSync;
      const previousSnapshot = triggerSync.snapshot(parseJobActions(previousJob));
      const currentSnapshot = triggerSync.snapshot(parseJobActions(sj));
      if (previousSnapshot.length > 0 || currentSnapshot.length > 0) {
        triggerSync.applyDiff({
          sourceType: "ScheduledJob",
          sourceId: sj.id,
          previousSnapshot,
          currentSnapshot
        });
      }

      if (previousJob) {
        sj = await (await ScheduledJobsService.Update(sj.id, sj)).data;
        this.loadScheduledJobs(this.state.project.id);
        return true;
      }

      let isCreated: boolean = false;
      try {
        const res: AxiosResponse = await ScheduledJobsService.Create(sj);
        // No toast: the name sheet's Create holds its ✓, then the new job's editor opens.
        isCreated = res?.status === 200;
      } catch (error) {
        KMainFunctions.exceptionHandler(error);
      }
      // Awaited, unlike an update's: the caller opens the new job next, and the editor can only
      // open a job the list already holds.
      await this.loadScheduledJobs(this.state.project.id);
      return isCreated;
    }
    return false;
  };

  addActionToScheduledJob = (action: IAction, scheduledJob: IScheduledJob) => {
    if (scheduledJob && action) {
      scheduledJob = _.cloneDeep(scheduledJob);
      if (scheduledJob.json === undefined) {
        scheduledJob.json = {};
      }
      if (scheduledJob.json && typeof scheduledJob.json === "string") {
        scheduledJob.json = JSON.parse(scheduledJob.json);
      }
      const { json } = scheduledJob;
      if (!json.actions || json?.actions?.length === 0) json.actions = [];
      const currentActionIndex = json?.actions.findIndex((a) => a.id == action.id);
      if (currentActionIndex > -1) {
        json.actions[currentActionIndex] = action;
      } else {
        json.actions?.push(action);
      }
      scheduledJob.json = JSON.stringify(json);

      this.saveScheduledJob(scheduledJob);
    }
  };

  deleteScheduleJobAction = (action: IAction, scheduledJob: IScheduledJob) => {
    if (scheduledJob && action) {
      // StartProcess trigger sync runs centrally in saveScheduledJob (whole-job diff),
      // which this method funnels into below — no per-action delete hook needed here.
      scheduledJob = _.cloneDeep(scheduledJob);
      if (scheduledJob.json === undefined) {
        scheduledJob.json = {};
      } else if (typeof scheduledJob.json === "string") {
        scheduledJob.json = JSON.parse(scheduledJob.json);
      }

      const { json } = scheduledJob;
      if (!json.actions) json.actions = [];
      const currentActionIndex = json?.actions.findIndex((a) => a.id == action.id);
      if (currentActionIndex > -1) {
        json.actions.splice(currentActionIndex, 1);
      }
      scheduledJob.json = JSON.stringify(json);

      this.saveScheduledJob(scheduledJob);
    }
  };

  setActiveScheduledJob = (sJobId: any) => {
    this.setState({ selectedSjId: sJobId }, () => {
      if (!sJobId) notifyDesignerLiveToolIdle();
    });
    if (sJobId) {
      const sjName: string | undefined = this.state.scheduledJobs?.find((sj) => sj.id === sJobId)?.name;
      DesignerPresenceReporter.reportEntity("scheduledJob", sJobId, sjName, "scheduledJob.changed");
    } else {
      DesignerPresenceReporter.reportEntity("scheduledJob", undefined, undefined);
    }
  };

  cancelEditingScheduledJob = () => {
    DesignerPresenceReporter.reportEntity("scheduledJob", undefined, undefined);
    this.setState({ selectedSjId: undefined }, () => notifyDesignerLiveToolIdle());
  };

  setCreateSJobActive = (val: boolean) => {
    this.setState({ isCreateSJobActive: val, selectedSjId: undefined });
  };

  setActiveCreatingSJobName = (name: string) => {
    this.setState({ activeCreatingSJobName: name });
    this.setCreateSJobActive(false);
  };
}
